import { Timeline } from "antd";
import { type ReactNode, useCallback, useState } from "react";
import { NIL as EMPTY_GUID } from "uuid";

import { BTSection } from "@btnet/core-components/BTSection";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import {
    BdsButton,
    BdsIconArrowCircleRight,
    BdsIconArrowsClockwise,
    BdsIconArrowUUpLeft,
    BdsIconCheckCircle,
    BdsIconClockCounterClockwise,
    BdsIconCurrencyCircleDollar,
    BdsIconExclamationCircle,
    BdsIconEye,
    BdsIconFileDocument,
    BdsIconMinusCircle,
    BdsIconNotePencil,
    BdsIconPaperPlaneRight,
    BdsIconPlusCircle,
    BdsIconProhibit,
    BdsIconStamp,
    BdsIconTray,
    BdsIconUser,
    BdsIconX,
    BdsIconXCircle,
    BdsLayout,
} from "@buildertrend/components";

import { EntityTypes } from "types/enum";

import { useLoadEffect } from "utilities/api.utilities";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSpin } from "commonComponents/btWrappers/BTSpin/BTSpin";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";

import {
    ActivityLogIconCategory,
    type EntityChangeEvent,
    entityChangeTrackingEntityChangesGetV2,
    entityChangeTrackingExternalEntityChangesByEntityShareTokenGetV2,
    SharedEntityType,
} from "handlers";

import { useSharedContent } from "entity/sharedContent/useSharedContent";

import "./ActivityLog.less";

import { type IActivityEvent, type IActivityLogProps } from "./ActivityLog.types";
import { ActivityLogItem } from "./ActivityLogItem";

// Maps client-side EntityTypes to the backend SharedEntityType used by the share-token endpoint.
// Only entity types that actually expose an external (share-token) activity log need an entry here.
const resolveSharedEntityType = (entityType: EntityTypes): SharedEntityType | undefined => {
    switch (entityType) {
        case EntityTypes.Submittal:
            return SharedEntityType.Submittal;
        default:
            return undefined;
    }
};

// Icon categories - map enum values to icons and styling
// These map to ActivityLogIconCategory enum from the backend
const ICON_CATEGORIES_BY_ENUM: Record<ActivityLogIconCategory, ReactNode> = {
    [ActivityLogIconCategory.Created]: <BdsIconPlusCircle className="created timeline-icon" />,
    [ActivityLogIconCategory.Updated]: <BdsIconNotePencil className="updated timeline-icon" />,
    [ActivityLogIconCategory.Sent]: <BdsIconPaperPlaneRight className="sent timeline-icon" />,
    [ActivityLogIconCategory.Approved]: <BdsIconCheckCircle className="approved timeline-icon" />,
    [ActivityLogIconCategory.Success]: <BdsIconCheckCircle className="paid timeline-icon" />,
    [ActivityLogIconCategory.Declined]: <BdsIconXCircle className="declined timeline-icon" />,
    [ActivityLogIconCategory.Failed]: <BdsIconXCircle className="rejected timeline-icon" />,
    [ActivityLogIconCategory.ReadyForPayment]: (
        <BdsIconArrowCircleRight className="ready timeline-icon" />
    ),
    [ActivityLogIconCategory.Paid]: <BdsIconCurrencyCircleDollar className="paid timeline-icon" />,
    [ActivityLogIconCategory.Pending]: (
        <BdsIconClockCounterClockwise className="ready timeline-icon" />
    ),
    [ActivityLogIconCategory.Processing]: (
        <BdsIconArrowsClockwise className="payment timeline-icon" />
    ),
    [ActivityLogIconCategory.SyncedToAccounting]: (
        <BdsIconArrowsClockwise className="sent timeline-icon" />
    ),
    [ActivityLogIconCategory.Closed]: <BdsIconCheckCircle className="approved timeline-icon" />,
    [ActivityLogIconCategory.ForReview]: <BdsIconEye className="ready timeline-icon" />,
    [ActivityLogIconCategory.AssignedTo]: <BdsIconUser className="updated timeline-icon" />,
    [ActivityLogIconCategory.Voided]: <BdsIconProhibit className="rejected timeline-icon" />,
    [ActivityLogIconCategory.ChangesRequested]: (
        <BdsIconExclamationCircle
            className="changes-requested timeline-icon"
            data-testid="changes-requested-icon"
        />
    ),
    [ActivityLogIconCategory.RevisionRequested]: (
        <BdsIconExclamationCircle className="timeline-icon" />
    ),
    [ActivityLogIconCategory.DocumentSubmitted]: (
        <BdsIconFileDocument className="approved timeline-icon" />
    ),
    [ActivityLogIconCategory.DocumentChangesRequested]: (
        <BdsIconArrowUUpLeft className="declined timeline-icon" />
    ),
    [ActivityLogIconCategory.Published]: <BdsIconStamp className="approved timeline-icon" />,
    [ActivityLogIconCategory.Returned]: <BdsIconTray className="timeline-icon" />,
    [ActivityLogIconCategory.Incomplete]: <BdsIconExclamationCircle className="timeline-icon" />,
    [ActivityLogIconCategory.Removed]: <BdsIconMinusCircle className="rejected timeline-icon" />,
    [ActivityLogIconCategory.Resent]: <BdsIconArrowsClockwise className="sent timeline-icon" />,
};

const getIconFromChange = (change: EntityChangeEvent): ReactNode => {
    // Use iconCategory from server if available
    if (change.iconCategory !== undefined && change.iconCategory !== null) {
        const icon = ICON_CATEGORIES_BY_ENUM[change.iconCategory];
        return icon;
    }

    // Default for generic updates
    return ICON_CATEGORIES_BY_ENUM[ActivityLogIconCategory.Updated];
};

const convertToActivityEvent = (change: EntityChangeEvent, index: number): IActivityEvent => {
    const description = change.changeDescription || "";
    const icon = getIconFromChange(change);

    // Convert property changes to changesMade format for backward compatibility
    const changesMade =
        change.propertyChanges?.map((prop) => {
            const displayName = prop.displayName ?? "";
            const oldValue = prop.oldValue ?? "(empty)";
            const newValue = prop.newValue ?? "(empty)";
            return `${displayName}: ${oldValue} → ${newValue}`;
        }) || null;

    return {
        id: `change-${index}`,
        icon,
        message: description,
        changesMade: changesMade && changesMade.length > 0 ? changesMade : null,
        propertyChanges: change.propertyChanges || null,
        relatedTypeChanges: change.relatedTypeChanges || null,
        operationType: change.operationType || null,
    };
};

export const ActivityLog: React.FC<IActivityLogProps> = ({
    entityId,
    externalEntityId,
    externalBuilderId,
    externalJobId,
    entityType,
    className,
    createdDateTime,
    createdBy,
    creationSource,
    entityTrackingDate,
    onClose,
    filter,
    hideHeader,
    detailsLabel,
}) => {
    const [entityChanges, setEntityChanges] = useState<EntityChangeEvent[]>([]);
    const [loading, setLoading] = useState(false);

    const { shareToken } = useSharedContent();

    const fetchEntityChanges = useCallback(
        async (handleLoadException: (e: unknown) => void) => {
            setLoading(true);

            try {
                let response;
                if (shareToken) {
                    const sharedEntityType = resolveSharedEntityType(entityType);
                    if (sharedEntityType === undefined) {
                        setEntityChanges([]);
                        return;
                    }
                    response =
                        await entityChangeTrackingExternalEntityChangesByEntityShareTokenGetV2(
                            shareToken,
                            { sharedEntityType, filter },
                        );
                } else {
                    response = await entityChangeTrackingEntityChangesGetV2({
                        externalEntityId,
                        entityId,
                        externalBuilderId,
                        entityType,
                        filter,
                        externalJobId: externalJobId === EMPTY_GUID ? undefined : externalJobId,
                    });
                }

                const dataChanges = response?.data?.changes ?? [];
                setEntityChanges(dataChanges);
            } catch (error) {
                handleLoadException(error);
                setEntityChanges([]);
            } finally {
                setLoading(false);
            }
        },
        [
            shareToken,
            externalBuilderId,
            externalEntityId,
            externalJobId,
            entityType,
            entityId,
            filter,
        ],
    );

    useLoadEffect(
        (handleLoadException) => {
            void fetchEntityChanges(handleLoadException);
        },
        [fetchEntityChanges],
    );

    // The server emits the initial creation as a change with operationType "CREATE". We keep rendering
    // the "Created by … / Source" header on the client (it carries the localized source label), but
    // reuse that event's field changes and nested related-type changes (e.g. line items added on
    // creation) so they appear under the Created entry instead of as a separate "Updated" event.
    const serverCreationChange = entityChanges.find((change) => change.operationType === "CREATE");
    const serverCreationEvent = serverCreationChange
        ? convertToActivityEvent(serverCreationChange, -1)
        : null;

    // Bills created via AI Bill Capture (OCR autofill) get a " using AI Bill Capture" suffix on the
    // creation line. The flag is set server-side on the CREATE event. Third-party integrations (e.g.
    // Home Depot) are ingested through OCR internally but must be attributed to the source alone, so
    // they never show the suffix.
    const wasAiBillCapture = serverCreationChange?.wasAiBillCapture ?? false;
    const isThirdPartyIntegration = creationSource === "Home Depot";
    const showAiBillCaptureSuffix = wasAiBillCapture && !isThirdPartyIntegration;

    // Filtering by groupKey is now done server-side
    const activityEvents = entityChanges
        .filter((change) => change.operationType !== "CREATE")
        .map((change: EntityChangeEvent, index: number) => convertToActivityEvent(change, index));

    // Add creation event at the beginning to ensure it always shows at the bottom
    // Timeline component has reverse=true, so the first item in the array displays at the bottom
    if (createdDateTime && createdBy) {
        const creationEvent: IActivityEvent = {
            id: "created",
            icon: <BdsIconPlusCircle className="created timeline-icon" />,
            message: (
                <div>
                    Created by {createdBy} on{" "}
                    <DateDisplayDate data-testid="created-date" value={createdDateTime} showTime />
                    {showAiBillCaptureSuffix && " using AI Bill Capture"}
                </div>
            ),
            changesMade: serverCreationEvent?.changesMade ?? null,
            propertyChanges: serverCreationEvent?.propertyChanges ?? null,
            relatedTypeChanges: serverCreationEvent?.relatedTypeChanges ?? null,
        };
        activityEvents.unshift(creationEvent);
    }

    const getWarningMessage = () => {
        if (createdDateTime && new Date(createdDateTime) < entityTrackingDate) {
            return (
                <>
                    Activity data tracked starting{" "}
                    <DateDisplayDate data-testid="entityTrackingDate" value={entityTrackingDate} />
                </>
            );
        }
        return null;
    };

    return (
        <BdsLayout
            contentAlign="compact"
            displayType="HCF-sm"
            id="activity-log-section"
            className="activity-log-section"
            testid="activity-log-section"
            useMaxWidth
        >
            {!hideHeader ? (
                <BTSection
                    id="activity-log-section-header"
                    data-testid="activity-log-section-header"
                    className="p-0"
                >
                    <BTRow justify="space-between" align="middle">
                        <BTCol>
                            <BTRow align="middle">
                                <BTText
                                    size="heavy-sm"
                                    text="Activity"
                                    className="margin-left-xs"
                                />
                            </BTRow>
                        </BTCol>
                        <BdsButton
                            displayType="tertiary"
                            icon={<BdsIconX />}
                            ariaLabel="Toggle split view off"
                            id="toggleSplitViewOff"
                            onClick={onClose}
                            testid="toggleSplitViewOff"
                        />
                    </BTRow>
                </BTSection>
            ) : (
                <></>
            )}
            <BTSection className={cn("p-0", "activity-log", className)}>
                {getWarningMessage() && (
                    <BTAlert
                        type="info"
                        message={getWarningMessage()}
                        data-testid="activity-log-info"
                        showIcon
                    />
                )}
                <BTSpin spinning={loading}>
                    <Timeline reverse>
                        {activityEvents.map((event, index) => (
                            <ActivityLogItem
                                key={`event-${event.id}`}
                                event={event}
                                index={index}
                                detailsLabel={detailsLabel}
                            />
                        ))}
                    </Timeline>
                </BTSpin>
            </BTSection>
        </BdsLayout>
    );
};
